import { StarIcon } from "lucide-react"
import { Button } from "@/components/shared/ui/button"
import { Link } from "@/navigation"
import { useTranslations } from "next-intl"
import { ProductCardProps } from "@/types/productTypes"
import Image from "next/image"
import { memo } from "react"

const ProductCard = memo(({ product }: { product: ProductCardProps }) => {
    const t = useTranslations("Products")
    
    // Fallback image for when product image is missing
    const fallbackImage = "/logos/logo.webp"
    
    return (
        <div className="flex flex-col shadow-2xl rounded-3xl h-full transform transition-all duration-300 hover:scale-[1.02]">
            <div className="w-full h-[250px] md:h-[200px] relative rounded-t-3xl overflow-hidden">
                {product?.productImage ? (
                    <Image
                        src={product.productImage}
                        alt={product.title || "Product Image"}
                        className="object-cover"
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        quality={85}
                        loading="lazy"
                        placeholder="blur"
                        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJSEkMjU1LS0yMi4qQEBALkE2Qjc4QD1AOTpBRUFETkhATUFBTkhBQUH/2wBDAR..."
                        onError={(e: any) => {
                            e.currentTarget.src = fallbackImage;
                        }}
                    />
                ) : (
                    <Image
                        src={fallbackImage}
                        alt={product.title || "Product Image"}
                        className="object-cover"
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        quality={85}
                        loading="lazy"
                    />
                )}
            </div>

            <div className="w-full p-4 flex flex-col gap-6 flex-grow">
                <div>
                    <h2 className="font-bold text-xl line-clamp-2">
                        {product.title || t("untitledProduct")}
                    </h2>

                    <p className="mt-3 w-full text-sub_text_color text-sub_text font-normal line-clamp-3">
                        {product.description || t("noDescription")}
                    </p>
                </div>

                <div className="w-full flex items-center justify-between mt-auto">
                    <span className="font-extrabold text-2xl leading-6">
                        ${product.price || 0}
                    </span>
                    <div className="flex items-center">
                        <span className="text-xl">
                            {product.averageRating || 0}
                        </span>
                        <StarIcon className="w-full px-1" color="#FACC15" fill="#FACC15" />
                    </div>
                </div>

                <Link href={`/checkout/${product._id}`} className="w-full" prefetch={false}>
                    <Button 
                        variant={"sub_primary"} 
                        className="w-full py-6 rounded-xl text-xl transform transition-all duration-300 hover:scale-105"
                    >
                        {t("buyNow")}
                    </Button>
                </Link>
            </div>
        </div>
    )
})

ProductCard.displayName = 'ProductCard'

export default ProductCard